import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useDebounce } from 'lib_ui-primitives';
import useMultiSelect from '../../../hooks/useMultiSelect';
import { getColumnHNodes, getColumnWidths } from '../columns/useColumnWidths';
import lodash from 'lodash';

const { isEqual, get } = lodash;

/**
 * Resolve a color-property value off an item, accounting for foreign-relation columns
 * whose joined data lives at `${prefix}.${field}`, where `prefix` is built as
 * `[propertyPath, namespace, relation].filter(Boolean).join(':')` and `propertyPath`
 * may be omitted (see getColumnCell.js for the same prefix logic).
 */
function resolveColorPropertyValue(item, colHNode, colorProperty) {
    if (!colorProperty) return undefined;
    if (colHNode.namespace && colHNode.relation) {
        const prefix = [colHNode.propertyPath, colHNode.namespace, colHNode.relation].filter(Boolean).join(':');
        return get(item, `${prefix}.${colorProperty}`);
    }
    return get(item, colorProperty);
}

// jsDom does not have a layout engine, so scrollLayout needs to be defaulted
// during testing (otherwise list/row clientWidths will be zero and no columns
// will be rendered).
const defaultClientWidth = __UNIT_TESTING__ ? 1500 : 0;

/**
 * Shared business logic for useRowDetail hook (web and native)
 * Handles column virtualization, scroll management, and selection state
 */
export function useRowDetailLogic(hNode, _columnHNodes, onRowClick, endOfRecordsText, PatchDetailTableComponent) {
    if (hNode.children == null) {
        throw new Error(`A ${hNode.hNodeType} must contain metadata columns.`);
    }
    
    const [scrollLayout, setScrollLayout] = useState({ clientWidth: defaultClientWidth, scrollLeft: 0 });
    // Separate horizontal scroll state: only changes when scrollLeft or clientWidth change.
    // This prevents RowDetail from recreating on vertical scroll events (scrollTop changes).
    const [horizontalScroll, setHorizontalScroll] = useState({ clientWidth: defaultClientWidth, scrollLeft: 0 });
    const [activeRowId, setActiveRowId] = useState(null);

    const onScrollLayoutChange = useDebounce(
        scrollLayout => {
            // Prevent a rerender when the layout individual values are the same, but the
            // reference has changed.
            setScrollLayout(previous => {
                if (
                    previous.clientHeight !== scrollLayout.clientHeight ||
                    previous.clientWidth !== scrollLayout.clientWidth ||
                    previous.scrollHeight !== scrollLayout.scrollHeight ||
                    previous.scrollWidth !== scrollLayout.scrollWidth ||
                    previous.scrollTop !== scrollLayout.scrollTop ||
                    previous.scrollLeft !== scrollLayout.scrollLeft
                ) {
                    return scrollLayout;
                }
                return previous;
            });
            // Only update horizontal scroll when horizontal values actually change
            setHorizontalScroll(prev => {
                if (prev.clientWidth !== scrollLayout.clientWidth || prev.scrollLeft !== scrollLayout.scrollLeft) {
                    return { clientWidth: scrollLayout.clientWidth, scrollLeft: scrollLayout.scrollLeft };
                }
                return prev;
            });
        },
        [],
        100
    );
    
    const [visibleColumnIndexes, setVisibleColumnIndexes] = useState([]);
    const [prefixColumnBufferWidth, setPrefixColumnBufferWidth] = useState(0);
    const [suffixColumnBufferWidth, setSuffixColumnBufferWidth] = useState(0);
    
    const theme = useTheme();
    const { children, allowCheckUnknownTags = false } = hNode;

    const columnHNodes = useMemo(() => getColumnHNodes(_columnHNodes, children), [_columnHNodes, children]);
    const columnWidths = useMemo(() => getColumnWidths(columnHNodes, theme), [columnHNodes, theme]);

    // Determine which columns should be rendered in the current visible window.
    // On mobile, buffer extends 1 viewport width on each side so horizontal scrolling
    // doesn't trigger column re-renders until the user scrolls past the pre-rendered area.
    const isMobile = theme?.mobile || false;
    useEffect(() => {
        const { clientWidth, scrollLeft } = horizontalScroll;
        const buffer = isMobile ? (clientWidth || 0) : 0;
        const windowStart = Math.max(0, (scrollLeft || 0) - buffer);
        const windowEnd = (scrollLeft || 0) + (clientWidth || 0) + buffer;
        const visibleColumnIndexes = [];
        let runningTotal = 0;
        let _prefixColumnBufferWidth = 0;
        let _suffixColumnBufferWidth = 0;
        for (let i = 0; i < columnWidths.length; i++) {
            const width = columnWidths[i];
            const columnStart = runningTotal;
            const columnEnd = runningTotal + width;
            if (columnEnd > windowStart && columnStart < windowEnd) {
                visibleColumnIndexes.push(i);
            } else if (columnEnd <= windowStart) {
                _prefixColumnBufferWidth += width;
            } else if (columnStart >= windowEnd) {
                _suffixColumnBufferWidth += width;
            } else {
                throw new Error('There is a problem with the column width calculations.');
            }
            runningTotal = columnEnd;
        }
        setPrefixColumnBufferWidth(_prefixColumnBufferWidth);
        setSuffixColumnBufferWidth(_suffixColumnBufferWidth);
        setVisibleColumnIndexes(prev => {
            if (!isEqual(visibleColumnIndexes, prev)) {
                return visibleColumnIndexes;
            }
            return prev;
        });

        // ** Useful for debugging: For measuring framerates with ALL columns rendered
        // setPrefixColumnBufferWidth(0);
        // setSuffixColumnBufferWidth(0);
        // setVisibleColumnIndexes([...Array(columnWidths.length).keys()]);

        // *** Useful for debugging, but not worth the clutter otherwise. ***
        // logging.debug('[useRowDetail] column names: ', columnHNodes.map(c => c.title).join(', '));
        // logging.debug('[useRowDetail] visibleColumnIndexes', JSON.stringify(visibleColumnIndexes));
    }, [horizontalScroll, columnWidths, isMobile]);

    const { includeCheckbox, singleRowSelect, namespace, relation } = hNode;
    const { onIndividualChange, isSelected, isSelectionDisabled } = useMultiSelect({ namespace, relation, singleRowSelect });

    return {
        // State
        scrollLayout,
        horizontalScroll,
        activeRowId,
        setActiveRowId,
        visibleColumnIndexes,
        prefixColumnBufferWidth,
        suffixColumnBufferWidth,
        
        // Column data
        columnHNodes,
        columnWidths,
        
        // Theme and config
        theme,
        allowCheckUnknownTags,
        includeCheckbox,
        singleRowSelect,
        namespace,
        relation,
        
        // Selection handlers
        onIndividualChange,
        isSelected,
        isSelectionDisabled,
        
        // Callbacks
        onScrollLayoutChange,
        onRowClick,
        endOfRecordsText,
        PatchDetailTableComponent
    };
}

/**
 * Calculate visible columns for column override scenario (two-grid scrollable)
 */
export function calculateVisibleColumns(columnHNodesOverride, overrideVisibleIndexes, visibleColumnIndexes,
    activeColumnWidths, scrollLayout, prefixColumnBufferWidth, suffixColumnBufferWidth, isMobile) {

    // Calculate visible indices and buffer widths for override columns if needed
    let calculatedVisibleIndexes = overrideVisibleIndexes || visibleColumnIndexes;
    let activePrefixWidth = prefixColumnBufferWidth;
    let activeSuffixWidth = suffixColumnBufferWidth;

    if (columnHNodesOverride && !overrideVisibleIndexes) {
        // For scrollable grid with override: calculate which are visible
        const { clientWidth, scrollLeft } = scrollLayout;

        // If not measured yet (clientWidth === 0), render all columns
        if (clientWidth === 0) {
            calculatedVisibleIndexes = Array.from({ length: activeColumnWidths.length }, (_, i) => i);
            activePrefixWidth = 0;
            activeSuffixWidth = 0;
        } else {
            // On mobile, buffer extends 1 viewport width on each side to reduce
            // column re-renders during horizontal scroll on low-powered devices.
            const buffer = isMobile ? clientWidth : 0;
            const windowStart = Math.max(0, scrollLeft - buffer);
            const windowEnd = scrollLeft + clientWidth + buffer;
            calculatedVisibleIndexes = [];
            activePrefixWidth = 0;
            activeSuffixWidth = 0;
            let runningTotal = 0;
            for (let i = 0; i < activeColumnWidths.length; i++) {
                const width = activeColumnWidths[i];
                const columnStart = runningTotal;
                const columnEnd = runningTotal + width;
                if (columnEnd > windowStart && columnStart < windowEnd) {
                    calculatedVisibleIndexes.push(i);
                } else if (columnEnd <= windowStart) {
                    activePrefixWidth += width;
                } else if (columnStart >= windowEnd) {
                    activeSuffixWidth += width;
                }
                runningTotal = columnEnd;
            }
        }
    }
    
    return { calculatedVisibleIndexes, activePrefixWidth, activeSuffixWidth };
}

/**
 * Shared item state calculations
 */
export function getItemState(item, allowCheckUnknownTags, isSelected) {
    const _id = item._id;
    const isUnknown = item.inDatabase === false;
    const disabledCheckbox = isUnknown && !allowCheckUnknownTags;
    // Never allowed a disabled row checkbox to be selected
    const selected = !disabledCheckbox && isSelected(_id);
    
    return { _id, isUnknown, disabledCheckbox, selected };
}

/**
 * Create onClick handler for checkbox/radio
 */
export function createOnClickHandler(_id, disabledCheckbox, selected, onIndividualChange, onRowClick, singleRowSelect) {
    // This is actually a component that will be rendered so useCallback is ok.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback(
        value => {
            if (disabledCheckbox) return;
            onIndividualChange(_id, value);
            if (value && !selected && singleRowSelect) {
                onRowClick?.({ _id });
            }
        },
        [_id, disabledCheckbox, selected, onIndividualChange, onRowClick, singleRowSelect]
    );
}

/**
 * Utility function to determine flag styling based on column configuration
 */
export function getFlagStyle(item, columnHNodes = []) {
    // Returns null (not {}) when no flags match, so callers can use a simple truthy check
    // to avoid unnecessary object spreads on every row.
    return columnHNodes.reduce((style, column) => {
        if (column.visuallyFlagOnValue) {
            // TODO: we probably want to allow for different styling options
            // Ideally, we'd get this from (a list of conditional) styles on the column definition.
            let styling = { ...style, backgroundColor: '#C6110B40' };
            // current: hack: for summary lines (e.g. Freight Runner Shift Summary) we want to bold the text and a red line on the left
            if (['Shift Summary', 'Summary'].includes(column.visuallyFlagOnValue)) {
                // Use separate border properties for React Native compatibility
                styling = { 
                    ...style, 
                    borderLeftWidth: 6, 
                    borderLeftColor: '#C6110B',
                    borderStyle: 'solid',
                    fontWeight: 'bold' 
                };
            }

            const value = item[column.propertyName];
            if (column.dataType === 'mappedBoolean') {
                if (
                    (value === false &&
                        ['false', column.mappedFalseValue.toLowerCase()].includes(
                            column.visuallyFlagOnValue.toLowerCase()
                        )) ||
                    (value === true &&
                        ['true', column.mappedTrueValue.toLowerCase()].includes(
                            column.visuallyFlagOnValue.toLowerCase()
                        )) ||
                    (typeof value === 'undefined' &&
                        ['', 'undefined', 'null'].includes(column.visuallyFlagOnValue.toLowerCase()))
                ) {
                    return styling;
                }
            } else if (value && value.toString().trim() === column.visuallyFlagOnValue.trim()) {
                return styling;
            }
        }
        return style;
    }, null);
}

/**
 * Utility function to determine row highlight styling based on display modifier configuration.
 * Checks column hNodes for `rowHighlight` property, resolves the color name from the item data,
 * and returns background/foreground styles for the row. When `resolveBorderColor` is supplied,
 * also returns longhand border-bottom props so the row separator stays visible against the
 * highlighted background (using longhand for React Native compatibility).
 *
 * @param {object} item - The record being rendered
 * @param {Array} columnHNodes - Column hNode definitions
 * @param {Function} resolveColor - Color resolution function (colorName, darkMode) => { background, foreground } | null
 * @param {boolean} darkMode - Whether dark mode is active
 * @param {Function} [resolveBorderColor] - Optional border color resolver (colorName, darkMode) => string | null
 * @returns {{ backgroundColor?: string, color?: string, borderBottomWidth?: number, borderBottomColor?: string, borderBottomStyle?: string }}
 */
export function getRowHighlightStyle(item, columnHNodes = [], resolveColor, darkMode, resolveBorderColor) {
    for (const column of columnHNodes) {
        if (column.rowHighlight) {
            const colorName = resolveColorPropertyValue(item, column, column.rowHighlight);
            const resolved = resolveColor(colorName, darkMode);
            if (resolved) {
                const style = { backgroundColor: resolved.background, color: resolved.foreground };
                // Re-color the row separator so it stays visible against the highlighted background.
                // Using longhand props rather than the `borderBottom` shorthand so this is valid on
                // React Native (which doesn't accept the CSS shorthand string). On web TableList the
                // longhands win over the existing `border-bottom: 1px solid ...` via cascade; on web
                // CardListRow (which has no border of its own) `borderBottomStyle: 'solid'` is needed
                // because the CSS default is `none` — RN silently ignores unknown style keys, so
                // including it is safe on both platforms.
                const borderColor = resolveBorderColor && resolveBorderColor(colorName, darkMode);
                if (borderColor) {
                    style.borderBottomWidth = 1;
                    style.borderBottomColor = borderColor;
                    style.borderBottomStyle = 'solid';
                }
                return style;
            }
        }
    }
    return {};
}

/**
 * Resolve pill box styling for a single cell.
 * Returns style object if the column has a `pillBox` display modifier and the item has a color value.
 *
 * @param {object} item - The record being rendered
 * @param {object} colHNode - The column hNode definition
 * @param {Function} resolveColor - Color resolution function
 * @param {boolean} darkMode - Whether dark mode is active
 * @returns {{ backgroundColor?: string, color?: string, borderRadius?: number, paddingLeft?: number, paddingRight?: number, paddingTop?: number, paddingBottom?: number } | null}
 */
export function getPillBoxStyle(item, colHNode, resolveColor, darkMode, resolveBorderColor) {
    if (!colHNode.pillBox) return null;
    const colorName = resolveColorPropertyValue(item, colHNode, colHNode.pillBox);
    const resolved = resolveColor(colorName, darkMode);
    if (!resolved) return null;
    const style = {
        backgroundColor: resolved.background,
        color: resolved.foreground,
        borderRadius: 9999,
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 2,
        paddingBottom: 2
    };
    const borderColor = resolveBorderColor && resolveBorderColor(colorName, darkMode);
    if (borderColor) {
        style.borderWidth = 1;
        style.borderStyle = 'solid';
        style.borderColor = borderColor;
    }
    return style;
}

// Export for testing
export const _private = { defaultClientWidth };
